/*
 * patch-picker.js — "Make it yours" add-on picker.
 *
 * Adds the selected add-ons to the cart in one /cart/add.js call, then re-submits
 * the product form so the theme's own handler adds the main product and opens the
 * side drawer exactly as it always does. Nothing here touches the drawer itself.
 *
 * With no add-ons selected the submit listener returns immediately, so the normal
 * add-to-cart path is completely unchanged.
 */
(function () {
  if (customElements.get('patch-picker')) return;

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(cents, precision, thousands, decimal) {
    var value = (cents / 100).toFixed(precision);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, format) {
    var match = String(format || '').match(PLACEHOLDER);
    if (!match) return (cents / 100).toFixed(2);
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(PLACEHOLDER, value);
  }

  function cartRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  /*
   * Per-patch zoom. The photos are framed differently (the artwork spans 11%
   * to 58% of the frame width), so one fixed zoom either clips the big patches
   * or leaves the small ones tiny. Each photo is measured once against its
   * cream canvas and zoomed so its furthest point sits at FIT_FILL of the
   * circle's radius, centred. The images are same-origin (/cdn/shop/files),
   * so the canvas is readable; if measuring fails, the CSS default zoom stays.
   */
  var FIT_FILL = 0.9;
  var FIT_MIN = 1;
  var FIT_MAX = 2.6;
  var FIT_W = 96;
  var fitCache = {};

  function percentile(sorted, f) {
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * f))];
  }

  function measureFit(img) {
    var w = img.naturalWidth;
    var h = img.naturalHeight;
    if (!w || !h) return null;
    var cw = FIT_W;
    var ch = Math.max(1, Math.round(FIT_W * h / w));
    var canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, cw, ch);
    var data = ctx.getImageData(0, 0, cw, ch).data;

    // The canvas colour is the median of the border pixels.
    var edge = [[], [], []];
    function sample(x, y) {
      var i = (y * cw + x) * 4;
      edge[0].push(data[i]); edge[1].push(data[i + 1]); edge[2].push(data[i + 2]);
    }
    for (var ex = 0; ex < cw; ex++) { sample(ex, 0); sample(ex, ch - 1); }
    for (var ey = 0; ey < ch; ey++) { sample(0, ey); sample(cw - 1, ey); }
    var bg = edge.map(function (c) { c.sort(function (a, b) { return a - b; }); return c[c.length >> 1]; });

    var xs = [];
    var ys = [];
    for (var y = 0; y < ch; y++) {
      for (var x = 0; x < cw; x++) {
        var i = (y * cw + x) * 4;
        if (Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) > 60) {
          xs.push(x);
          ys.push(y);
        }
      }
    }
    if (xs.length < 20) return null;

    // Trim stray pixels (dust, shadow specks) before taking the extent.
    var sx = Float32Array.from(xs).sort();
    var sy = Float32Array.from(ys).sort();
    var cx = (percentile(sx, 0.005) + percentile(sx, 0.995) + 1) / 2;
    var cy = (percentile(sy, 0.005) + percentile(sy, 0.995) + 1) / 2;
    var dist = new Float32Array(xs.length);
    for (var k = 0; k < xs.length; k++) dist[k] = Math.hypot(xs[k] + 0.5 - cx, ys[k] + 0.5 - cy);
    dist.sort();
    var r = percentile(dist, 0.995) + 0.5;

    // object-fit: cover in a square box: the shorter side spans the box.
    var aspect = w / h;
    var dw = aspect <= 1 ? 1 : aspect;
    var dh = aspect <= 1 ? 1 / aspect : 1;
    var unit = dw / cw;
    var bx = cx * unit - (dw - 1) / 2;
    var by = cy * unit - (dh - 1) / 2;
    var scale = Math.max(FIT_MIN, Math.min(FIT_MAX, (FIT_FILL * 0.5) / (r * unit)));
    return { s: scale, x: (0.5 - bx) * scale, y: (0.5 - by) * scale };
  }

  function applyFit(img, fit) {
    img.style.setProperty('--pp-zoom', fit.s.toFixed(3));
    img.style.setProperty('--pp-x', (fit.x * 100).toFixed(2) + '%');
    img.style.setProperty('--pp-y', (fit.y * 100).toFixed(2) + '%');
    img.setAttribute('data-fit', '');
  }

  function fitImage(img) {
    if (!img || img.hasAttribute('data-fit')) return;
    function run() {
      var key = String(img.currentSrc || img.src).split('?')[0];
      var fit = fitCache[key];
      if (!fit) {
        try { fit = measureFit(img); } catch (e) { fit = null; }
        if (fit) fitCache[key] = fit;
      }
      if (fit) applyFit(img, fit);
      else img.setAttribute('data-fit', 'default');
    }
    if (img.complete && img.naturalWidth) {
      run();
    } else {
      img.addEventListener('load', run, { once: true });
      img.addEventListener('error', function () { img.setAttribute('data-fit', 'default'); }, { once: true });
    }
  }

  function fitAll(root) {
    root.querySelectorAll('.patch-tile__clip img').forEach(fitImage);
  }

  class PatchPicker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.selected = new Map();
      this.gridLoaded = false;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      // 'included': the patches are part of a set's price. Exactly `required`
      // pieces, no prices, and the set (gallery-set.js) owns the cart add.
      this.included = this.dataset.mode === 'included';
      this.required = Number(this.dataset.required) || 0;

      this.modal = this.querySelector('[data-patch-modal]');
      this.body = this.querySelector('[data-patch-body]');
      this.chipLists = this.querySelectorAll('[data-patch-chips]');
      this.selectionPanes = this.querySelectorAll('[data-patch-selection]');
      this.countPill = this.querySelector('[data-patch-count]');
      this.errorEl = this.querySelector('[data-patch-error]');
      this.modalCount = this.querySelector('[data-patch-modal-count]');
      this.modalMaths = this.querySelector('[data-patch-modal-maths]');
      this.clearBtn = this.querySelector('[data-patch-clear]');
      var stringsEl = this.querySelector('[data-patch-strings]');
      this.strings = Object.assign(
        {
          one: '1 patch',
          many: '[count] patches',
          remove: 'Remove [title]',
          increase: 'Add another [title]',
          decrease: 'Remove one [title]',
          none: 'No patches selected',
          caseTotal: 'Total [base] \u2192 [total]',
          loadError: 'Could not load the full range. Please try again.',
          genericError: 'Could not add to cart. Please try again.',
          droppedSuffix: 'It has been removed from your selection \u2014 tap add again.',
          ofRequired: '[count] of [required] chosen',
          capReached: 'That\u2019s [required]. Remove one to swap it.'
        },
        stringsEl ? JSON.parse(stringsEl.textContent) : {}
      );

      this.addEventListener('click', this.onClick.bind(this));

      if (this.modal) {
        this.modal.addEventListener('click', function (event) {
          if (event.target === this.modal) this.modal.close();
        }.bind(this));
      }

      if (!this.included) {
        // Both the main buy form and the mobile sticky bar must carry the add-ons.
        var scope = this.closest('.product-page') || document;
        this.forms = Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"]'));
        this.forms.forEach(function (form) {
          form.addEventListener('submit', this.onSubmit.bind(this), { capture: true });
        }, this);

        this.bindButtonTotal();
      }
      fitAll(this);
      this.prefetchGrid();
      this.render();
    }

    /*
     * The full grid is a separate section render (0.3-1.3s on Shopify's side).
     * Start it while the shopper is still looking at the strip, so "See all"
     * opens on a ready grid. Its images stay lazy: inside the closed dialog
     * they don't load until it opens.
     */
    prefetchGrid() {
      if (!('IntersectionObserver' in window) || !this.querySelector('[data-patch-open]')) return;
      var io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 200); };
        idle(function () { this.loadGrid().catch(function () {}); }.bind(this));
      }.bind(this), { rootMargin: '400px 0px' });
      io.observe(this);
    }

    loadGrid() {
      if (!this._grid) {
        this._grid = fetch(window.location.pathname + '?sections=patch-grid')
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          })
          .then(function (data) {
            var parsed = new DOMParser().parseFromString(data['patch-grid'] || '', 'text/html');
            var grid = parsed.querySelector('[data-patch-grid]');
            if (!grid) throw new Error('grid missing');
            return grid;
          });
        // A failed prefetch mustn't stick: opening the modal retries.
        this._grid.catch(function () { this._grid = null; }.bind(this));
      }
      return this._grid;
    }

    onClick(event) {
      var step = event.target.closest('[data-patch-step]');
      if (step) {
        this.step(step.dataset.variantId, Number(step.dataset.patchStep));
        return;
      }
      var remove = event.target.closest('[data-patch-remove]');
      if (remove) {
        this.selected.delete(remove.dataset.variantId);
        this.setError('');
        this.render();
        return;
      }
      var tile = event.target.closest('[data-patch-tile]');
      if (tile && !tile.disabled) {
        this.toggle(tile);
        return;
      }
      if (event.target.closest('[data-patch-open]')) {
        this.openModal();
        return;
      }
      if (event.target.closest('[data-patch-clear]')) {
        this.clearSelection();
        return;
      }
      if (event.target.closest('[data-patch-close]')) {
        this.modal.close();
        return;
      }
      var filter = event.target.closest('[data-patch-filter]');
      if (filter) this.setFilter(filter.dataset.patchFilter);
    }

    pieces() {
      var n = 0;
      this.selected.forEach(function (item) {
        n += item.qty;
      });
      return n;
    }

    /* In included mode the set fixes the count; adding past it is refused, not queued. */
    atCap() {
      if (!this.included || this.pieces() < this.required) return false;
      this.setError(this.strings.capReached.replace('[required]', this.required));
      return true;
    }

    toggle(tile) {
      var id = tile.dataset.variantId;
      if (this.selected.has(id)) {
        this.selected.delete(id);
      } else {
        if (this.atCap()) return;
        this.selected.set(id, {
          id: Number(id),
          title: tile.dataset.title,
          price: Number(tile.dataset.price) || 0,
          image: tile.dataset.image,
          crop: tile.hasAttribute('data-crop'),
          qty: 1
        });
      }
      this.setError('');
      this.render();
    }

    /* No upper bound by design. Stepping below one removes the patch outright. */
    step(id, delta) {
      var item = this.selected.get(id);
      if (!item) return;
      if (delta > 0 && this.atCap()) return;
      var next = item.qty + delta;
      if (next < 1) this.selected.delete(id);
      else item.qty = next;
      this.setError('');
      this.render();
    }

    render() {
      var selected = this.selected;

      // Tiles are toggles; the badge carries the count once it is above one.
      this.querySelectorAll('.patch-tile[data-patch-tile]').forEach(function (tile) {
        var item = selected.get(tile.dataset.variantId);
        tile.setAttribute('aria-pressed', item ? 'true' : 'false');
        tile.classList.toggle('is-selected', !!item);
        var check = tile.querySelector('.patch-tile__check');
        if (check) check.textContent = item && item.qty > 1 ? String(item.qty) : '\u2713';
      });

      // "3 patches" means three pieces in the bag, not three distinct designs.
      var pieces = 0;
      var subtotal = 0;
      selected.forEach(function (item) {
        pieces += item.qty;
        subtotal += item.price * item.qty;
      });

      this.selectionPanes.forEach(function (pane) {
        pane.hidden = selected.size === 0;
      });
      this.chipLists.forEach(function (list) {
        list.innerHTML = '';
        selected.forEach(function (item, id) {
          list.appendChild(this.buildChip(item, id));
        }, this);
      }, this);

      var noun = pieces === 1 ? this.strings.one : this.strings.many.replace('[count]', pieces);
      var base = this.baseCents();

      if (this.included) {
        var progress = this.strings.ofRequired.replace('[count]', pieces).replace('[required]', this.required);
        var capped = pieces >= this.required;
        if (this.countPill) {
          this.countPill.hidden = false;
          this.countPill.textContent = progress;
          this.countPill.classList.toggle('is-complete', capped);
        }
        if (this.modalCount) this.modalCount.textContent = progress;
        if (this.modalMaths) this.modalMaths.textContent = '';
        // Dim what can't be added any more, so the cap reads before anyone taps.
        this.querySelectorAll('.patch-tile[data-patch-tile]').forEach(function (tile) {
          tile.classList.toggle('is-capped', capped && !selected.has(tile.dataset.variantId));
        });
        if (!capped) this.setError('');
      } else {
        if (this.countPill) {
          this.countPill.hidden = pieces === 0;
          this.countPill.textContent = noun + ' \u00b7 +' + formatMoney(subtotal, this.moneyFormat);
        }
        if (this.modalCount) {
          this.modalCount.textContent = pieces
            ? noun + ' \u00b7 +' + formatMoney(subtotal, this.moneyFormat)
            : this.strings.none;
        }
        if (this.modalMaths) {
          this.modalMaths.textContent = pieces
            ? this.strings.caseTotal
                .replace('[base]', formatMoney(base, this.moneyFormat))
                .replace('[total]', formatMoney(base + subtotal, this.moneyFormat))
            : '';
        }
      }
      if (this.clearBtn) this.clearBtn.hidden = pieces === 0;

      this.paintButtonTotal();

      var items = [];
      selected.forEach(function (item) {
        items.push({ id: item.id, title: item.title, qty: item.qty });
      });
      this.dispatchEvent(new CustomEvent('patch-picker:change', {
        bubbles: true,
        detail: {
          items: items,
          pieces: pieces,
          required: this.required,
          complete: this.included ? pieces === this.required : true
        }
      }));
    }

    buildChip(item, id) {
      var short = item.title.replace('Stacked Store ', '').replace(' Patch', '');
      var li = document.createElement('li');
      li.className = 'patch-chip';

      var clip = document.createElement('span');
      clip.className = 'patch-chip__clip';
      var img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      clip.appendChild(img);
      // A pre-cropped image is already framed; only full photos need measuring.
      if (item.crop) img.setAttribute('data-fit', 'crop');
      else fitImage(img);
      li.appendChild(clip);

      var label = document.createElement('span');
      label.className = 'patch-chip__label';
      label.textContent = short;
      li.appendChild(label);

      var step = document.createElement('span');
      step.className = 'patch-chip__step';
      step.appendChild(this.stepButton(id, -1, this.strings.decrease.replace('[title]', short)));

      var qty = document.createElement('span');
      qty.className = 'patch-chip__qty';
      qty.textContent = item.qty;
      step.appendChild(qty);

      step.appendChild(this.stepButton(id, 1, this.strings.increase.replace('[title]', short)));
      li.appendChild(step);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'patch-chip__remove';
      remove.setAttribute('data-patch-remove', '');
      remove.setAttribute('data-variant-id', id);
      remove.setAttribute('aria-label', this.strings.remove.replace('[title]', short));
      remove.textContent = '\u00d7';
      li.appendChild(remove);

      return li;
    }

    stepButton(id, delta, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'patch-chip__stepbtn';
      b.setAttribute('data-patch-step', delta);
      b.setAttribute('data-variant-id', id);
      b.setAttribute('aria-label', label);
      b.textContent = delta > 0 ? '+' : '\u2212';
      return b;
    }

    /*
     * The theme prints the running price into `.dynamic-price-value` and
     * rewrites it whenever quantity changes. Rather than fight that, observe it:
     * when the theme writes, re-add the patch total on top. The `_writing` flag
     * stops our own write from retriggering the observer.
     */
    bindButtonTotal() {
      var scope = this.closest('.product-page') || document;
      this.priceEl = scope.querySelector('product-form .dynamic-price-value') ||
        scope.querySelector('.dynamic-price-value');
      if (!this.priceEl) return;
      this.priceFormat = this.priceEl.dataset.moneyFormat || this.moneyFormat;
      this.qtyEl = scope.querySelector('product-form input[name="quantity"]');
      this._obs = new MutationObserver(function () {
        if (this._writing) return;
        this.paintButtonTotal();
      }.bind(this));
      this._obs.observe(this.priceEl, { childList: true, characterData: true, subtree: true });
    }

    baseCents() {
      if (!this.priceEl) return 0;
      var unit = Number(this.priceEl.dataset.unitPriceCents) || 0;
      var qty = Number(this.qtyEl && this.qtyEl.value) || 1;
      return unit * (qty > 0 ? qty : 1);
    }

    paintButtonTotal() {
      if (!this.priceEl) return;
      var sum = 0;
      this.selected.forEach(function (item) {
        sum += item.price * item.qty;
      });
      var text = formatMoney(this.baseCents() + sum, this.priceFormat);
      if (this.priceEl.textContent.trim() === text) return;
      this._writing = true;
      this.priceEl.textContent = text;
      window.requestAnimationFrame(function () {
        this._writing = false;
      }.bind(this));
    }

    async openModal() {
      this.modal.showModal();
      if (this.gridLoaded) return;

      try {
        var grid = await this.loadGrid();
        if (this.gridLoaded) return;
        this.body.innerHTML = '';
        this.body.appendChild(grid);
        fitAll(grid);
        this.gridLoaded = true;
        this.render();
      } catch (err) {
        this.body.innerHTML = '<p class="patch-modal__loading">' + this.strings.loadError + '</p>';
      }
    }

    setFilter(group) {
      this.querySelectorAll('[data-patch-filter]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.dataset.patchFilter === group ? 'true' : 'false');
      });
      this.applyFilters();
    }

    applyFilters() {
      var active = this.querySelector('[data-patch-filter][aria-pressed="true"]');
      var group = active ? active.dataset.patchFilter : 'all';
      var visible = 0;

      this.querySelectorAll('.patch-grid__item').forEach(function (item) {
        var show = group === 'all' || item.dataset.group === group;
        item.hidden = !show;
        if (show) visible += 1;
      });

      var empty = this.querySelector('[data-patch-empty]');
      if (empty) empty.hidden = visible !== 0;
    }

    clearSelection() {
      this.selected.clear();
      this.render();
    }

    disconnectedCallback() {
      if (this._obs) this._obs.disconnect();
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    setBusy(form, busy) {
      var button = form.querySelector('[type="submit"]');
      if (!button) return;
      button.classList.toggle('loading', busy);
      if (busy) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    /*
     * Add the product and its patches in ONE request.
     *
     * Earlier this deferred the main product to the theme by replaying a submit
     * or a click. Both wake two separate handlers bound to the same form
     * (product.js's <product-form> and CartJS), which added the product twice,
     * intermittently. Posting every line ourselves is deterministic.
     *
     * CartJS.addItems posts a single {items:[...]} through the theme's own
     * queue, so the drawer opens and the cross-sell box refreshes exactly as
     * they do for a normal add.
     */
    onSubmit(event) {
      if (this.selected.size === 0) return; // nothing chosen — leave the theme alone

      event.preventDefault();
      event.stopImmediatePropagation();

      var form = event.currentTarget;
      var self = this;
      this.setError('');
      this.setBusy(form, true);

      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      var items = [];
      if (idInput && idInput.value) {
        items.push({ id: Number(idInput.value), quantity: Number(qtyInput && qtyInput.value) || 1 });
      }
      this.selected.forEach(function (item) {
        items.push({ id: item.id, quantity: item.qty });
      });

      if (window.CartJS && typeof window.CartJS.addItems === 'function') {
        window.CartJS.addItems(items, {
          success: function () {
            self.clearSelection();
            self.setBusy(form, false);
          },
          error: function (xhr) {
            var body = {};
            try { body = JSON.parse(xhr && xhr.responseText); } catch (e) {}
            self.handleAddError(body);
            self.setBusy(form, false);
          }
        });
        return;
      }

      // No CartJS: post it ourselves and open the drawer the way the theme does.
      fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (res.ok) return null;
          return res.json().catch(function () { return {}; });
        })
        .then(function (err) {
          if (err) {
            self.handleAddError(err);
            return;
          }
          self.clearSelection();
          document.body.classList.add('open-cart');
        })
        .catch(function () {
          self.setError(self.strings.genericError);
        })
        .finally(function () {
          self.setBusy(form, false);
        });
    }

    handleAddError(err) {
      var message = err.description || err.message || this.strings.genericError;
      var dropped = null;

      // Every add-on is DENY-policy, so one out-of-stock item fails the whole
      // call. Drop whichever one Shopify named so a retry can succeed.
      this.selected.forEach(function (item, id) {
        if (!dropped && message.indexOf(item.title) !== -1) {
          dropped = item.title;
          this.selected.delete(id);
        }
      }, this);

      this.render();
      this.setError(dropped ? message + ' ' + this.strings.droppedSuffix : message);
    }
  }

  customElements.define('patch-picker', PatchPicker);
})();
